import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertIcon,
  ArrowIcon,
  AuthSplitShell,
  CheckIcon,
} from '../components/layout/AuthSplitShell.js';
import { client } from '../main.js';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ForgotPasswordPage() {
  const errorRef = useRef<HTMLDivElement>(null);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const normalizedEmail = email.trim();
  const formValid = EMAIL_PATTERN.test(normalizedEmail);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!formValid || loading) return;
    setError(null);
    setLoading(true);
    try {
      await client.auth.forgotPassword({
        email: normalizedEmail,
        redirectUrl: window.location.origin,
      });
      setSubmitted(true);
    } catch (err) {
      setError((err as Error).message || 'Something went wrong.');
      requestAnimationFrame(() => errorRef.current?.focus());
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <AuthSplitShell
        eyebrow="Check your inbox"
        headline={
          <>
            Link sent.
            <br />
            <em>Go find it.</em>
          </>
        }
        description={
          <>
            <strong>The reset link expires in one hour.</strong> If it doesn’t
            arrive, check spam before requesting another.
          </>
        }
      >
        <div className="auth-form">
          <h1>Link sent</h1>
          <p className="auth-subtitle">
            If an account exists for <strong>{normalizedEmail}</strong>, we’ve
            sent a reset link.
          </p>

          <div className="auth-note">
            <CheckIcon />
            <p>Didn’t get it? Check spam, or try again in a minute.</p>
          </div>

          <Link
            to="/login"
            className="auth-primary-button auth-login-button"
            style={{ textDecoration: 'none' }}
          >
            Back to sign in <ArrowIcon />
          </Link>
        </div>
      </AuthSplitShell>
    );
  }

  return (
    <AuthSplitShell
      eyebrow="Password reset"
      headline={
        <>
          Locked out?
          <br />
          <em>Happens to everyone.</em>
        </>
      }
      description={
        <>
          <strong>Enter the email you signed up with</strong> and we’ll send a
          link to set a new password.
        </>
      }
    >
      <form className="auth-form" noValidate onSubmit={(e) => void onSubmit(e)}>
        <h1>Forgot password</h1>
        <p className="auth-subtitle">
          We’ll email you a link to set a new one.
        </p>

        {error && (
          <div ref={errorRef} className="auth-banner" role="alert" tabIndex={-1}>
            <AlertIcon />
            <p>{error}</p>
          </div>
        )}

        <label htmlFor="email">Email address</label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="username"
          placeholder="you@proptr.ee"
          value={email}
          disabled={loading}
          autoFocus
          onChange={(e) => {
            setEmail(e.target.value);
            setError(null);
          }}
        />

        <button
          type="submit"
          className="auth-primary-button auth-login-button"
          disabled={!formValid || loading}
          aria-busy={loading}
        >
          {loading ? (
            <>
              <span className="auth-spinner" aria-hidden="true" /> Sending
            </>
          ) : (
            <>
              Send reset link <ArrowIcon />
            </>
          )}
        </button>

        <div className="auth-alt-action" style={{ justifyContent: 'center' }}>
          <Link to="/login">Back to sign in</Link>
        </div>
      </form>
    </AuthSplitShell>
  );
}
